import IconeImpedimento from '@mui/icons-material/PanTool';
import ThumbDown from '@mui/icons-material/ThumbDown';
import { Box, Tooltip, Typography } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';

import { SITUACAO_IMPEDIMENTOS_PROCESSO } from '@/dominio/SituacaoImpedimentosProcesso';

import PopoverSecretaria from '../PopoverSecretaria';
import ReactComponent from '../ReactComponent';

class Impedimento extends ReactComponent {
    abrirPopoverImpedimentosDeclarados = false;
    abrirPopoverImpedimentosPossiveis = false;
    acabouDeFechar = false;
    anchorEl = null;

    handleClickImpedimentosPossiveis = async (event) => {
        if (this.acabouDeFechar) {
            return;
        }

        const anchorElement = event.currentTarget;
        await this.buscarMotivosImpedimentos();
        runInAction(() => {
            this.abrirPopoverImpedimentosPossiveis = true;
            this.anchorEl = anchorElement;
        });
    };

    handleClickImpedimentosDeclarados = async (event) => {
        if (this.acabouDeFechar) {
            return;
        }

        const anchorElement = event.currentTarget;
        await this.buscarMotivosImpedimentos();
        runInAction(() => {
            this.abrirPopoverImpedimentosDeclarados = true;
            this.anchorEl = anchorElement;
        });
    };

    handleCloseImpedimentosPossiveis = () => {
        this.acabouDeFechar = true;
        setTimeout(() => {
            runInAction(() => {
                this.acabouDeFechar = false;
            });
        }, 10);
        this.abrirPopoverImpedimentosPossiveis = false;
        this.anchorEl = null;
    };

    handleCloseImpedimentosDeclarados = () => {
        this.acabouDeFechar = true;
        setTimeout(() => {
            runInAction(() => {
                this.acabouDeFechar = false;
            });
        }, 10);
        this.abrirPopoverImpedimentosDeclarados = false;
        this.anchorEl = null;
    };

    renderListaImpedimentos = (titulo, impedimentos) =>
        impedimentos.length > 0 && (
            <Box>
                <Typography
                    color="primary"
                    style={{ margin: '6px' }}
                    variant="h6"
                >
                    {titulo}
                </Typography>
                <List>
                    {impedimentos.map((impedimento) => (
                        <ListItem key={this.nextKey}>
                            {impedimento.magistrado.sigla}
                            {impedimento.motivos ? ' - ' + impedimento.motivos : ''}
                        </ListItem>
                    ))}
                </List>
            </Box>
        );

    constructor(props) {
        super(props);

        makeObservable(this, {
            abrirPopoverImpedimentosDeclarados: observable,
            abrirPopoverImpedimentosPossiveis: observable,
            acabouDeFechar: observable,
            anchorEl: observable,
            handleClickImpedimentosPossiveis: action,
            handleClickImpedimentosDeclarados: action,
            handleCloseImpedimentosPossiveis: action,
            handleCloseImpedimentosDeclarados: action,
            buscarMotivosImpedimentos: action,
            isFerramentasDetalhes: computed,
            registro: computed,
            todosImpedimentosTratados: computed,
            impedimentos: computed,
            existeImpedimentoNaComposicao: computed,
            existeImpedimentoDeclaradoComposicao: computed,
            existeImpedimentoDeclaradoForaComposicao: computed,
            existeImpedimentoPossivelComposicao: computed,
            existeImpedimentoPossivelForaComposicao: computed,
            existemImpedimentosPossiveis: computed,
            existemImpedimentosDeclarados: computed,
            impedimentosDeclaradosComposicao: computed,
            impedimentosDeclaradosForaComposicao: computed,
            impedimentosPossiveisComposicao: computed,
            impedimentosPossiveisForaComposicao: computed,
            impedimentosComposicao: computed,
            impedimentosForaComposicao: computed,
            renderListaImpedimentos: false,
            isImpedimentoComposicao: false,
            isImpedimentoForaComposicao: false,
            renderImpedimentosPossiveis: false,
            renderImpedimentosDeclarados: false,
            render: false
        });
    }

    async buscarMotivosImpedimentos() {
        const impedimentosMotivos = await this.sessaoService.buscarMotivosImpedimentos(
            this.registro.sessaoId || this.store.idPautaSelecionada,
            this.registro.processo.numeroCompleto
        );

        if (impedimentosMotivos) {
            runInAction(() => {
                for (const impedimento of this.impedimentos) {
                    for (const impedimentoMotivo of impedimentosMotivos) {
                        const isMesmaSigla = impedimentoMotivo.magistrado.sigla === impedimento.magistrado.sigla;
                        const mesmoCpf =
                            impedimentoMotivo.magistrado.cpf &&
                            impedimentoMotivo.magistrado.cpf === impedimento.magistrado.cpf;

                        if (isMesmaSigla || mesmoCpf) {
                            impedimento.motivos = impedimentoMotivo.motivos;
                        }
                    }
                }
            });
        }

        return impedimentosMotivos;
    }

    get isFerramentasDetalhes() {
        return this.observableProps.isferramentaDetalhes;
    }

    get registro() {
        return this.observableProps.registro;
    }

    /**
     * Retorna true se há impedimento na composição padrão e se o magistrado impedido não está na composição do processo.
     */
    get todosImpedimentosTratados() {
        const impedimentosComposicaoPadrao = this.salaSessaoStore.getImpedimentosComposicaoPadrao(this.registro) || [];

        if (impedimentosComposicaoPadrao.length === 0) {
            return false;
        }

        for (const impedimento of impedimentosComposicaoPadrao) {
            const impedimentoNaComposicao = this.registro.composicao.some(
                (magComp) => magComp.magistrado.sigla === impedimento.magistrado.sigla
            );

            if (impedimentoNaComposicao) {
                return false;
            }
        }

        return true;
    }

    get impedimentos() {
        return this.registro.impedimentos || [];
    }

    get existeImpedimentoNaComposicao() {
        return this.impedimentosComposicao.length > 0;
    }

    get existeImpedimentoDeclaradoComposicao() {
        return this.impedimentosDeclaradosComposicao.length > 0;
    }

    get existeImpedimentoDeclaradoForaComposicao() {
        return this.impedimentosDeclaradosForaComposicao.length > 0;
    }

    get existeImpedimentoPossivelComposicao() {
        return this.impedimentosPossiveisComposicao.length > 0;
    }

    get existeImpedimentoPossivelForaComposicao() {
        return this.impedimentosPossiveisForaComposicao.length > 0;
    }

    get existemImpedimentosPossiveis() {
        return this.impedimentosPossiveisComposicao.length > 0 || this.impedimentosPossiveisForaComposicao.length > 0;
    }

    get existemImpedimentosDeclarados() {
        return this.impedimentosDeclaradosComposicao.length > 0 || this.impedimentosDeclaradosForaComposicao.length > 0;
    }

    get impedimentosDeclaradosComposicao() {
        return this.impedimentosComposicao.filter(
            (impedimento) => impedimento.situacaoImpedimento === 'IMPEDIMENTO_DECLARADO'
        );
    }

    get impedimentosDeclaradosForaComposicao() {
        return this.impedimentosForaComposicao.filter(
            (impedimento) => impedimento.situacaoImpedimento === 'IMPEDIMENTO_DECLARADO'
        );
    }

    get impedimentosPossiveisComposicao() {
        return this.impedimentosComposicao.filter(
            (impedimento) => impedimento.situacaoImpedimento === 'IMPEDIMENTO_POSSIVEL'
        );
    }

    get impedimentosPossiveisForaComposicao() {
        return this.impedimentosForaComposicao.filter(
            (impedimento) => impedimento.situacaoImpedimento === 'IMPEDIMENTO_POSSIVEL'
        );
    }

    isImpedimentoComposicao(siglaImpedimento) {
        return this.registro.composicao.some(
            (composicao) =>
                (composicao.magistrado?.sigla === siglaImpedimento && !composicao.magistrado?.siglaSubstituto) ||
                composicao.magistrado?.siglaSubstituto === siglaImpedimento
        );
    }

    isImpedimentoForaComposicao(siglaImpedimento) {
        return !this.isImpedimentoComposicao(siglaImpedimento);
    }

    get impedimentosComposicao() {
        return this.impedimentos.filter(
            (impedimento) => impedimento.magistrado?.sigla && this.isImpedimentoComposicao(impedimento.magistrado.sigla)
        );
    }

    get impedimentosForaComposicao() {
        return this.impedimentos.filter((impedimento) => {
            return impedimento.magistrado?.sigla && this.isImpedimentoForaComposicao(impedimento.magistrado.sigla);
        });
    }

    renderImpedimentosPossiveis() {
        if (!this.existemImpedimentosPossiveis) {
            return null;
        }

        const situacao = this.existeImpedimentoPossivelComposicao
            ? SITUACAO_IMPEDIMENTOS_PROCESSO.POSSIVEL_IMPEDIMENTO_NA_COMPOSICAO
            : SITUACAO_IMPEDIMENTOS_PROCESSO.POSSIVEL_IMPEDIMENTO_FORA_DA_COMPOSICAO;

        return (
            <div style={{ display: 'flex', alignItems: 'center', width: '28px', height: '30px', marginLeft: '8px' }}>
                <Tooltip title="Possíveis impedimentos">
                    <ThumbDown
                        style={{
                            margin: '3px',
                            fillOpacity: situacao.opacidade,
                            color: situacao.cor,
                            transform: 'scaleX(-1)'
                        }}
                        onClick={this.handleClickImpedimentosPossiveis}
                    />
                </Tooltip>
                {this.abrirPopoverImpedimentosPossiveis ? (
                    <PopoverSecretaria
                        anchorEl={this.anchorEl}
                        onClose={this.handleCloseImpedimentosPossiveis}
                        open={this.abrirPopoverImpedimentosPossiveis}
                    >
                        {this.renderListaImpedimentos(
                            'Possíveis impedimentos cadastrados na composição do processo:',
                            this.impedimentosPossiveisComposicao
                        )}
                        {this.renderListaImpedimentos(
                            'Possíveis impedimentos cadastrados fora composição do processo:',
                            this.impedimentosPossiveisForaComposicao
                        )}
                    </PopoverSecretaria>
                ) : null}
            </div>
        );
    }

    renderImpedimentosDeclarados() {
        if (!this.existemImpedimentosDeclarados) {
            return null;
        }

        const situacao = this.existeImpedimentoDeclaradoComposicao
            ? SITUACAO_IMPEDIMENTOS_PROCESSO.IMPEDIMENTO_DECLARADO_NA_COMPOSICAO
            : SITUACAO_IMPEDIMENTOS_PROCESSO.IMPEDIMENTO_DECLARADO_FORA_DA_COMPOSICAO;

        return (
            <div style={{ display: 'flex', width: '28px', marginLeft: '8px', height: 'auto' }}>
                <Tooltip title="Impedimentos declarados">
                    <IconeImpedimento
                        style={{
                            cursor: 'pointer',
                            fillOpacity: situacao.opacidade,
                            color: situacao.cor
                        }}
                        onClick={this.handleClickImpedimentosDeclarados}
                    />
                </Tooltip>
                {this.abrirPopoverImpedimentosDeclarados ? (
                    <PopoverSecretaria
                        anchorEl={this.anchorEl}
                        onClose={this.handleCloseImpedimentosDeclarados}
                        open={this.abrirPopoverImpedimentosDeclarados}
                    >
                        {this.renderListaImpedimentos(
                            'Impedimentos declarados cadastrados na composição do processo:',
                            this.impedimentosDeclaradosComposicao
                        )}
                        {this.renderListaImpedimentos(
                            'Impedimentos declarados cadastrados fora da composição do processo:',
                            this.impedimentosDeclaradosForaComposicao
                        )}
                    </PopoverSecretaria>
                ) : null}
            </div>
        );
    }

    render() {
        const width = this.isFerramentasDetalhes ? '' : '52px';

        return (
            <div style={{ display: 'flex', alignItems: 'center', width }}>
                {this.renderImpedimentosPossiveis()}
                {this.renderImpedimentosDeclarados()}
            </div>
        );
    }
}

export default inject('stores')(observer(Impedimento));
